/**
 * The centre agenda drawn as a calendar: which colour each therapist is drawn
 * in, and where overlapping cards sit. The days themselves are the shared
 * calendar grid's, re-exported here.
 *
 * Kept free of React so the arithmetic can be tested without rendering.
 */

import { BRAND_BOOKING_COLOR } from "../../../shared/ui/calendar/bookingCardStyle";

export type { CalendarView as AgendaCalendarView } from "../../../shared/domain/calendarGrid";
export {
    bookingsByDay,
    calendarRange,
    initialsOf,
    isBookingPast,
    minuteLabel,
    minutesOfTime,
    mondayOf,
    monthWeeks,
    shiftAnchor,
    shiftDays,
    weekDays,
} from "../../../shared/domain/calendarGrid";
export type { CalendarBooking } from "../../../shared/domain/calendarGrid";

/**
 * One colour per therapist. The first six are the design system's chart
 * series (`--chart-1` … `--chart-6`, in that order); a centre usually has
 * more therapists than a chart has series, so four further hues follow
 * before the list starts over. Colour is never a therapist's only cue: every
 * card also carries their name or initials.
 */
export const THERAPIST_COLORS = [
    BRAND_BOOKING_COLOR,
    "#0d9488",
    "#ec4a0a",
    "#0086c9",
    "#db2777",
    "#b54708",
    "#4d7c0f",
    "#be123c",
    "#475569",
    "#7e22ce",
] as const;

/**
 * Therapist id → colour, in the order the ids are given.
 *
 * The caller hands in the team in a stable order, so a therapist keeps their
 * colour from one week, month or reload to the next. An id repeated later in
 * the list keeps the colour it was first given.
 */
export function assignTherapistColors(
    therapistIds: readonly string[]
): Map<string, string> {
    const colors = new Map<string, string>();
    therapistIds.forEach((id) => {
        if (colors.has(id)) return;
        colors.set(id, THERAPIST_COLORS[colors.size % THERAPIST_COLORS.length]);
    });
    return colors;
}

/**
 * Where a card sits across its day, in percent, when it shares the time with
 * others: each one starts in its own lane but stretches over the next, as
 * Google Calendar draws them, so a card keeps room for its name. The last
 * lane, drawn on top, keeps its own width.
 */
export function overlappingCardBox(lane: number, lanes: number): { left: number; width: number } {
    const share = 100 / lanes;
    const left = lane * share;
    if (lanes === 1 || lane === lanes - 1) return { left, width: share };
    return { left, width: Math.min(100 - left, share * 1.7) };
}
